import React from "react";

import clsx from "clsx";

export type StatusStripTone =
  | "masquerade"
  | "simulate-permissions"
  | "preview"
  | "preview-real-data";

// The hover overlay strength is tuned per tone so button text stays above
// 4.5:1 while hovered; see the strip tokens in tailwind.css.
const TONE_CLASSES: Record<StatusStripTone, { strip: string; hover: string }> =
  {
    masquerade: {
      strip: "bg-masquerade-strip text-masquerade-strip-text",
      hover: "hover:bg-masquerade-strip-text/20",
    },
    "simulate-permissions": {
      strip:
        "bg-simulate-permissions-strip text-simulate-permissions-strip-text",
      hover: "hover:bg-simulate-permissions-strip-text/10",
    },
    preview: {
      strip: "bg-preview-strip text-preview-strip-text",
      hover: "hover:bg-preview-strip-text/10",
    },
    "preview-real-data": {
      strip: "bg-preview-strip-real-data text-preview-strip-real-data-text",
      hover: "hover:bg-preview-strip-real-data-text/20",
    },
  };

export function statusStripHoverClassName(tone: StatusStripTone): string {
  return TONE_CLASSES[tone].hover;
}

export function StatusStrip({
  tone,
  label,
  icon,
  children,
}: {
  tone: StatusStripTone;
  label: string;
  icon: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div
      role="region"
      aria-label={label}
      className={clsx(
        "flex h-7 w-full items-center gap-2.5 px-4 text-[13px] leading-4 font-medium",
        TONE_CLASSES[tone].strip,
      )}
    >
      {icon}
      {children}
    </div>
  );
}

export function StatusStripButton({
  tone,
  className,
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement> & {
  tone: StatusStripTone;
}) {
  return (
    <button
      className={clsx(
        "h-5 shrink-0 cursor-pointer rounded-sm border border-current px-2 text-[11px] font-semibold tracking-[.02em] uppercase",
        TONE_CLASSES[tone].hover,
        className,
      )}
      {...props}
    />
  );
}
